<template>
    <TprmModal
        :additional-class-name="modalClassName"
        :title="title"
        :subtitle="description"
        open
        allow-close
        @close="onCloseRequest"
    >
        <p v-if="ssoRoleWarning" class="UserManagementConfigureUserModal__ssoWarning" data-test="sso-invite-warning">
            If this user signs in through your identity provider, their role will be set by your group mapping
            rather than the role selected here. Invitations remain the way to add users outside your identity provider.
        </p>
        <div class="UserManagementConfigureUserModal__columns">
            <JsonSchemaForm
                v-model="inputValue"
                class-name="UserManagementConfigureUserModal__form"
                simulated
                :disabled="submitting"
                :form="form"
            />
        </div>
        <template #footer>
            <NewButton
                variant="fill"
                color="primary"
                :loading="submitting"
                :disabled="!submittable"
                @click="onSubmit"
            >
                {{ submitLabel }}
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
.UserManagementConfigureUserModal {
    &__columns {
        display: flex;
        flex-direction: column;
        align-content: stretch;
        justify-content: center;
    }

    &__ssoWarning {
        margin: 0 0 var(--spacing-3);
        color: var(--eidTechGray);
        font-size: var(--text-caption-size);
    }

    &__form {
        flex: 1 auto;
    }

    .FormElement__requiredIndicator {
        color: inherit;
        font-style: italic;
    }

    .JsonSchemaForm__hint,
    .JsonSchemaForm__warning {
        font-style: italic;
    }

    .FormInput__actionContainer {
        display: none;
    }

    .NewDropdown .q-field__control {
        background-color: #fff;
        height: 45px;
        padding: 0 15px;

        &:hover::before {
            border: 1px solid #aaa;
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import isEqual from 'lodash/isEqual';

    import { JsonSchemaForm } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormObject } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { RbacRole, RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac';
    import { toRbacRole } from '@evidentid/tprm-portal-lib/utils/common/toRbacRole';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    import { UserInput } from '../types';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): migrated to the Vue 3 v-model
    // contract. Consumed via v-model by EditUserProcedure/InviteUserProcedure.
    // The `onChange` emit's second argument (`this.submittable`) is not part of the
    // v-model contract -- neither parent listens with an explicit handler that
    // captures it, so it was already effectively unused under the v2 contract too.
    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit'/
    // 'update:modelValue' but it is neither declared in the emits option nor as an
    // 'onClose'/... prop" every time it fires.
    @Component({
        components: {
            NewButton,
            TprmModal,
            JsonSchemaForm,
        },
        emits: [ 'close', 'submit', 'update:modelValue' ],
    })
    export default class ConfigureUserModal extends Vue {
        // PRODUCT-26110 QA fixes5: procedure-injected attrs across modal chains --
        // EditUserProcedure/InviteUserProcedure pass `:rp-name="rpName"` and a bare `open`
        // down onto this component (the procedure itself already has direct access to
        // `rpName` for its own store calls and never reads it back off this modal; `open`
        // is likewise never toggled -- the procedure unmounts this whole component via
        // ProcedureManager instead of flipping `open` false). Neither is consumed here,
        // and this component's single root (`TprmModal` -> `Modal`) ultimately resolves to
        // `Modal.vue`'s `<Teleport>` root, so an undeclared attr cascades all the way down
        // through the fallthrough chain and Vue warns "Extraneous non-props attributes"
        // there. Declared here (unused) purely to absorb them, same mechanism as the
        // `uuid` prop declared on every ProcedureManager-rendered procedure component.
        @Prop({ type: String, default: '' })
        private rpName?: string;

        @Prop({ type: Boolean, default: true })
        private open?: boolean;

        @Prop({ type: Boolean, default: false })
        private submitting!: boolean;

        @Prop({ type: Object, default: () => ({}) })
        private modelValue!: Partial<UserInput>;

        @Prop({ type: String })
        private title!: string;

        @Prop({ type: String })
        private description!: string;

        @Prop({ type: String, default: 'Save' })
        private submitLabel!: string;

        @Prop({ type: Boolean, default: false })
        private disableEmailAddress!: boolean;

        @Prop({ type: Boolean, default: false })
        private disableName!: boolean;

        @Prop({ type: String, default: null })
        private nameWarning!: string | null;

        @Prop({ type: Boolean, default: false })
        private isEdit!: boolean;

        @Prop({ type: Boolean, default: false })
        private ssoRoleWarning!: boolean;

        private form: JsonFormObject = null as any;

        private inputValue = {
            name: '',
            email: '',
            role: this.defaultRole,
        };

        private get roles(): RbacRoleDetails[] {
            // The `inputValue` field initializer reads this getter through `defaultRole`
            // while the component is still being constructed, so the roles request may not
            // have resolved yet. `initializeForm` re-derives the default role once it has.
            return useRbacStore().userRoles?.list || [];
        }

        private get defaultRole(): RbacRole | null {
            return this.roles.some((role) => role.name === toRbacRole('viewer')) ? toRbacRole('viewer') : null;
        }

        private get submittable(): boolean {
            return this.form.isValid(this.modelValue, true);
        }

        private get modalClassName(): string {
            return this.isEdit
                ? 'UserManagementConfigureUserModal UserManagementConfigureUserModal--edit'
                : 'UserManagementConfigureUserModal';
        }

        @Watch('roles', { immediate: true })
        private initializeForm(): void {
            // `inputValue.role` is initialized while `roles` is still empty, so the
            // default only becomes resolvable here, once the roles have loaded.
            if (!this.inputValue.role) {
                this.inputValue.role = this.defaultRole;
            }
            const roleProperty = {
                enum: this.roles.map((role) => role.name),
                enumLabels: this.roles.map((role) => role.label),
                title: 'Select Role',
            };

            this.form = createForm({
                type: JsonSchemaType.object,
                properties: {
                    name: {
                        type: JsonSchemaType.string,
                        readOnly: this.disableName,
                        format: 'non-email',
                        title: 'Name',
                        minLength: 1,
                        warning: this.nameWarning,
                    },
                    email: {
                        type: JsonSchemaType.string,
                        readOnly: this.disableEmailAddress,
                        format: 'email',
                        title: 'Email address',
                        description: 'Email address',
                        hint: 'Emails cannot be edited',
                        minLength: 1,
                    },
                    ...(this.isEdit ? {} : { role: roleProperty }),
                },
                required: [ 'name', 'email', 'role' ],
                propertiesOrder: [ 'email', 'name', 'role' ],
            });
        }

        @Watch('modelValue', { immediate: true })
        private updateValueExternally(): void {
            const nextInputValue = this.form.getValue({
                name: '',
                email: '',
                role: this.defaultRole,
                ...this.modelValue,
            }, true);

            // Do not trigger watcher if not necessary
            if (!isEqual(this.inputValue, nextInputValue)) {
                this.inputValue = nextInputValue;
            }
        }

        @Watch('inputValue')
        private onChange(): void {
            this.$emit('update:modelValue', this.inputValue, this.submittable);
        }

        private onCloseRequest(): void {
            this.$emit('close');
        }

        private onSubmit(): void {
            if (this.submittable) {
                this.$emit('submit', this.modelValue);
            }
        }
    }
</script>
